import { useMemo, useState } from 'react';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SectionHeading from '../../components/Common/SectionHeading';
import {
  exportCtaChecklist,
  exportProcessSteps,
  exportProductInterestOptions,
  exportReadiness,
  exportRegions,
  exportStats,
  exportWhyCards,
  pageContent,
} from '../../data/siteData';
import { submitNetlifyForm } from '../../utils/netlifyForms';

const exportFormName = 'export-enquiry';

const scrollToSection = (id) => {
  const section = document.getElementById(id);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

function ExportPage() {
  const content = pageContent.export;

  const initialForm = useMemo(
    () => ({
      name: '',
      company: '',
      country: '',
      email: '',
      phone: '',
      productInterest: '',
      message: '',
    }),
    []
  );
  const [formData, setFormData] = useState(initialForm);
  const [feedback, setFeedback] = useState({ open: false, severity: 'success', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      await submitNetlifyForm(exportFormName, formData);
      setFeedback({ open: true, severity: 'success', message: content.formSection.successMessage });
      setFormData(initialForm);
    } catch (error) {
      setFeedback({ open: true, severity: 'error', message: content.formSection.errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Export Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background:
            'radial-gradient(circle at 50% 20%, rgba(74,14,143,0.45) 0%, rgba(26,5,51,1) 45%, rgba(13,1,32,1) 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'repeating-linear-gradient(45deg, rgba(201,168,76,0.07) 0px, rgba(201,168,76,0.07) 1px, transparent 1px, transparent 32px)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 12, md: 14 } }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid item xs={12} lg={7}>
              <Stack spacing={3}>
                <Chip
                  label={content.hero.eyebrow}
                  sx={{
                    width: 'fit-content',
                    bgcolor: alpha('#c9a84c', 0.12),
                    color: '#f4dc97',
                    border: '1px solid rgba(201,168,76,0.24)',
                    fontWeight: 800,
                  }}
                />
                <Typography variant="h1" sx={{ color: '#ffffff', fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4.4rem' }, lineHeight: 1.05, maxWidth: 760 }}>
                  {content.hero.title}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.76)', maxWidth: 640, fontSize: { xs: '1rem', md: '1.12rem' }, lineHeight: 1.8 }}>
                  {content.hero.description}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => scrollToSection('export-form')}
                  >
                    {content.hero.primaryCta.label}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadRoundedIcon />}
                    onClick={() => scrollToSection('export-form')}
                    sx={{
                      color: '#ffffff',
                      borderColor: 'rgba(255,255,255,0.64)',
                      '&:hover': { borderColor: '#ffffff', backgroundColor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    {content.hero.secondaryCta.label}
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  bgcolor: 'rgba(255,255,255,0.09)',
                  color: '#ffffff',
                  border: '1px solid rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(18px)',
                  boxShadow: '0 25px 60px rgba(8,2,18,0.34)',
                }}
              >
                <Typography variant="h4" sx={{ color: '#ffffff', mb: 0.5 }}>
                  {content.hero.readinessTitle}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.68)', mb: 3 }}>
                  {content.hero.readinessSubtitle}
                </Typography>
                <Stack spacing={0}>
                  {exportReadiness.map((row, index) => (
                    <Stack
                      key={row.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        py: 1.75,
                        borderTop: index === 0 ? 'none' : '1px solid rgba(201,168,76,0.18)',
                      }}
                    >
                      <Typography sx={{ color: 'rgba(255,255,255,0.68)' }}>{row.label}</Typography>
                      <Typography sx={{ color: '#f4dc97', fontWeight: 800, textAlign: 'right' }}>
                        {row.value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Snapshot Row */}
      <Box sx={{ bgcolor: '#1a0533', py: { xs: 5, md: 6 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: { xs: 2, md: 3 },
            }}
          >
            {exportStats.map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  textAlign: 'center',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(201,168,76,0.18)',
                }}
              >
                <Typography variant="h4" sx={{ color: '#f4dc97', mb: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>{stat.label}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Why Export With SCIO */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={content.whySection.eyebrow}
            title={content.whySection.title}
            subtitle={content.whySection.subtitle}
          />
          <Grid container spacing={3} justifyContent="center">
            {exportWhyCards.map((card) => {
              const Icon = card.icon;
              return (
                <Grid item xs={12} sm={8} md={5} key={card.title}>
                  <Card elevation={0} sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3.5 }}>
                      <Box
                        sx={{
                          width: 62,
                          height: 62,
                          borderRadius: 3.5,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: '#efe7ff',
                          color: '#4a0e8f',
                          mb: 2.5,
                        }}
                      >
                        <Icon />
                      </Box>
                      <Typography variant="h4" sx={{ mb: 1.25 }}>
                        {card.title}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                        {card.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Export Process */}
      <Box sx={{ bgcolor: '#faf6ee', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={content.processSection.eyebrow}
            title={content.processSection.title}
            subtitle={content.processSection.subtitle}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'flex-start' },
            }}
          >
            {exportProcessSteps.map((step, index) => (
              <Box
                key={step.title}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: { xs: 'row', md: 'column' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  textAlign: { xs: 'left', md: 'center' },
                  gap: { xs: 2, md: 0 },
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    width: { xs: 'auto', md: '100%' },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      minWidth: 52,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: '#c9a84c',
                      color: '#1a0533',
                      fontWeight: 900,
                      fontSize: '1.15rem',
                      boxShadow: '0 8px 20px rgba(201,168,76,0.35)',
                      zIndex: 1,
                    }}
                  >
                    {index + 1}
                  </Box>
                  {index < exportProcessSteps.length - 1 ? (
                    <Box
                      sx={{
                        bgcolor: 'rgba(201,168,76,0.4)',
                        width: { xs: 2, md: '100%' },
                        height: { xs: 40, md: 2 },
                        my: { xs: 0, md: 0 },
                        mx: { xs: 0, md: 1.5 },
                        ml: { xs: '25px', md: 1.5 },
                      }}
                    />
                  ) : (
                    <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%' }} />
                  )}
                </Box>
                <Box sx={{ px: { xs: 0, md: 2 }, pt: { xs: 0, md: 2.5 }, pb: { xs: 3, md: 0 } }}>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Regions Open to Export */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <SectionHeading title={content.regionsSection.title} subtitle={content.regionsSection.subtitle} />
          <Stack direction="row" spacing={1.5} rowGap={1.5} flexWrap="wrap" justifyContent="center">
            {exportRegions.map((region) => (
              <Chip
                key={region}
                icon={<PublicRoundedIcon />}
                label={region}
                sx={{
                  bgcolor: '#fff6de',
                  color: '#4a0e8f',
                  border: '1px solid rgba(201,168,76,0.28)',
                  fontWeight: 700,
                  py: 2.5,
                  px: 0.5,
                  '& .MuiChip-icon': { color: '#c9a84c' },
                }}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* CTA Banner */}
      <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 6 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            color: '#ffffff',
            background: 'linear-gradient(135deg, #4a0e8f 0%, #1a0533 100%)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at top right, rgba(201,168,76,0.16), transparent 36%)',
            }}
          />
          <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid item xs={12} md={7}>
              <Typography variant="h3" sx={{ color: '#ffffff', mb: 1.2 }}>
                {content.ctaBanner.title}
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={1.4} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                {exportCtaChecklist.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Stack key={item.title} direction="row" spacing={1.25} alignItems="center">
                      <Icon sx={{ color: '#c9a84c' }} />
                      <Typography sx={{ color: '#ffffff' }}>{item.title}</Typography>
                    </Stack>
                  );
                })}
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1 }}
                  onClick={() => scrollToSection('export-form')}
                >
                  {content.ctaBanner.buttonLabel}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Export Enquiry Form */}
      <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 10 } }}>
        <Paper
          id="export-form"
          elevation={0}
          sx={{ p: { xs: 3, md: 4 }, scrollMarginTop: '88px' }}
        >
          <Typography variant="h3" sx={{ mb: 0.5 }}>
            {content.formSection.title}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 3 }}>{content.formSection.subtitle}</Typography>
          <Box
            component="form"
            name={exportFormName}
            method="post"
            action="/"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="form-name" value={exportFormName} />
            <input type="hidden" name="bot-field" />
            <Grid container spacing={2}>
              {content.formSection.fields
                .filter((field) => field.name !== 'message')
                .map((field) => (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <TextField
                      fullWidth
                      type={field.type || 'text'}
                      name={field.name}
                      label={field.label}
                      required={field.required}
                      value={formData[field.name]}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                ))}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  name="productInterest"
                  label={content.formSection.productInterestLabel}
                  value={formData.productInterest}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  {exportProductInterestOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {content.formSection.fields
                .filter((field) => field.name === 'message')
                .map((field) => (
                  <Grid item xs={12} key={field.name}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={field.rows}
                      name={field.name}
                      label={field.label}
                      required={field.required}
                      value={formData[field.name]}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                ))}
              <Grid item xs={12}>
                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                  {isSubmitting ? content.formSection.submittingLabel : content.formSection.submitLabel}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ExportPage;
